import * as Sentry from "@sentry/react";

const DSN = "https://8d4412948534e79ea3ad4a544c418be3@o4511585894465536.ingest.us.sentry.io/4511585903771648";

export function initSentry() {
  const environment = import.meta.env.DEV ? "development" : "production";
  const release = (import.meta.env.VITE_APP_RELEASE as string | undefined) || `vecurate@${import.meta.env.MODE}`;

  Sentry.init({
    dsn: DSN,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Mask all text/inputs by default; we'll unmask non-sensitive selectively if needed.
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
        // Mask any element with these selectors (review content, etc.)
        mask: [
          '[data-sentry-mask]',
          'textarea#review',
          'textarea[name="review"]',
          'input[type="password"]',
          'input[type="email"]',
          'input[name*="email" i]',
          'input[name*="password" i]',
          'input[name*="token" i]',
          'input[autocomplete="current-password"]',
          'input[autocomplete="new-password"]',
          'input[autocomplete="email"]',
        ],
        block: ['[data-sentry-block]'],
      }),
    ],
    tracesSampleRate: environment === "production" ? 0.2 : 1.0,
    replaysSessionSampleRate: environment === "production" ? 0.1 : 0.0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event) {
      return scrubEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "console" && breadcrumb.message) {
        breadcrumb.message = scrubString(breadcrumb.message);
      }
      if (breadcrumb.data) {
        for (const k of Object.keys(breadcrumb.data)) {
          const v = breadcrumb.data[k];
          if (typeof v === "string") breadcrumb.data[k] = scrubString(v);
        }
      }
      return breadcrumb;
    },
  });
}

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const BEARER_RE = /Bearer\s+[A-Za-z0-9._\-]+/gi;
const SENSITIVE_KEYS = /(password|token|secret|authorization|api[_-]?key|access[_-]?token|refresh[_-]?token|review)/i;

function scrubString(s: string): string {
  return s
    .replace(JWT_RE, "[redacted-token]")
    .replace(BEARER_RE, "Bearer [redacted]")
    .replace(EMAIL_RE, "[redacted-email]");
}

function scrubValue(v: unknown): unknown {
  if (typeof v === "string") return scrubString(v);
  if (Array.isArray(v)) return v.map(scrubValue);
  if (v && typeof v === "object") return scrubObject(v as Record<string, unknown>);
  return v;
}

function scrubObject(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.test(k)) {
      out[k] = "[redacted]";
    } else {
      out[k] = scrubValue(v);
    }
  }
  return out;
}

function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.request) {
    if (event.request.headers) event.request.headers = scrubObject(event.request.headers as any) as any;
    if (event.request.cookies) event.request.cookies = "[redacted]" as any;
    if (typeof event.request.data === "string") event.request.data = scrubString(event.request.data);
    else if (event.request.data && typeof event.request.data === "object")
      event.request.data = scrubObject(event.request.data as any);
    if (event.request.query_string && typeof event.request.query_string === "string")
      event.request.query_string = scrubString(event.request.query_string);
  }
  if (event.user) {
    // Drop PII; keep only id if present.
    event.user = event.user.id ? { id: event.user.id } : undefined;
  }
  if (event.extra) event.extra = scrubObject(event.extra) as any;
  if (event.contexts) event.contexts = scrubObject(event.contexts as any) as any;
  if (event.message) event.message = scrubString(event.message);
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubString(ex.value);
    }
  }
  return event;
}
